<script setup lang="ts">
import type { TNewsCard } from '~/components/entity/news/card/types'
import { useDateTimeFormat } from '~/composables/useDateTimeFormat'
import { FavoriteButton } from '~/components/ui/favorite-button'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<TNewsCard>(), {
  variant: 'light'
})

const formattedDate = computed(() =>
  props.publication_datetime ? useDateTimeFormat(props.publication_datetime) : ''
)
</script>

<template>
  <article
    class="NewsCard bg-white"
    :class="[$props.variant === 'dark' ? 'NewsCardDark' : '', $props.highlight ? 'Highlight' : '']"
  >
    <FavoriteButton v-if="!hideAddToFavorites" :id="props.id" variant="card" />

    <NuxtImg
      v-if="$props.picture"
      :src="$props.picture"
      width="350"
      height="210"
      :alt="$props.title"
      :loading="$props.eagerLoading ? 'eager' : 'lazy'"
      class="NewsCardImg"
      sizes="350px"
    />
    <div class="NewsCardContent">
      <header class="NewsCardHeader">
        <div>
          <div v-if="$props.category" class="NewsCardCategoryWrapper">
            <Badge rounded="full" class="NewsCardCategoryBadge">{{ $props.category }}</Badge>
          </div>
        </div>
        <div>
          <span v-if="formattedDate" class="NewsCardDate">{{ formattedDate }}</span>
        </div>
      </header>
      <Heading tag="h3" class="h4 NewsCardTitle">
        <NuxtLinkLocale class="hit-title NewsCardLink" :to="$props.url" v-html="$props.title" />
      </Heading>
      <p class="hit-title NewsCardSummary" v-html="$props.summary" />
      <span v-if="formattedDate" class="NewsCardDateMobile">{{ formattedDate }}</span>
    </div>
  </article>
</template>

<style scoped>
.NewsCard {
  @apply relative flex flex-col overflow-hidden rounded-lg border text-brand-gray ring-offset-white transition transition-shadow duration-300 hover:shadow-lg hover:ring-gray-300 sm:block;
}

.NewsCardDark {
  @apply border-brand-gray bg-brand-gray text-white ring-offset-brand-gray hover:ring-white;
}

.NewsCardImg {
  @apply w-full;
}

.NewsCardContent {
  @apply flex flex-1 flex-col justify-between p-4 sm:space-y-2.5;
}

.NewsCardHeader {
  @apply hidden items-center justify-between sm:flex;
}

.NewsCardCategoryWrapper {
  @apply flex gap-1.5;
}

.NewsCardCategoryBadge {
  @apply bg-blue-50 font-normal text-gray-900;
}

.NewsCardTitle {
  @apply font-sans text-[1rem] font-normal leading-5 sm:font-semibold;
}

.NewsCardLink {
  @apply after:absolute after:inset-0 hover:underline focus-visible:ring-0;
}

.NewsCardSummary {
  @apply hidden text-sm sm:block;
}

.NewsCardDate {
  @apply text-xs text-gray-500;
}

.NewsCardDateMobile {
  @apply mt-2 block text-xs text-gray-500 sm:hidden;
}
</style>
